import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { noCase } from 'change-case';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import T from 'src/basic-components/text';
import { useApi } from 'src/contexts/api-provider';
import useLocales from 'src/hooks/useLocales';
import Iconify from '../../../components/Iconify';
import MenuPopover from '../../../components/MenuPopover';
import Scrollbar from '../../../components/Scrollbar';
import { IconButtonAnimate } from '../../../components/animate';
import { fToNow } from '../../../utils/formatTime';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const [model, setModel] = useState();
  const { CoreApi } = useApi();
  const apiName = 'notificationsApi';
  const api = {
    unreads: CoreApi(apiName, 'unreads'),
  };
  useEffect(() => {
    if (model) return;
    api.unreads().then(setModel).catch(setModel);
  }, [model]);

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = () => {};

  if (!model)
    return (
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Iconify icon="eva:bell-fill" width={20} height={20} />
      </IconButtonAnimate>
    );

  return (
    <>
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={model.unreadCount} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">
              <T>notifications</T>
            </Typography>
            {model.unreadCount > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <T number={model.unreadCount}>you-have-@number-unread-messages</T>
              </Typography>
            )}
            {model.unreadCount === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <T>no-new-notification</T>
              </Typography>
            )}
          </Box>

          {model.unreadCount > 0 && (
            <Tooltip title=" Mark all as read">
              <IconButtonAnimate color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButtonAnimate>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {model.unReadMessages.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button href="/dashboard/notifications" fullWidth disableRipple>
            <T>view-all</T>
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    createdAt: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    isUnRead: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    avatar: PropTypes.any,
  }),
};

function NotificationItem({ notification }) {
  const { local } = useLocales();
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      href={`/dashboard/notifications/${notification.id}`}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(notification.isUnRead && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(notification.createdAt, local)}
            {/*  */}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Stack flex flexDirection="column" spacing={0}>
      <Typography variant="subtitle2">{notification.title}</Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {noCase(notification.description)}
      </Typography>
    </Stack>
  );

  return {
    avatar: <img alt={notification.title} src="/icons/ic_notification_chat.svg" />,
    title,
  };
}
